import React, { useState } from 'react';
import { CityState } from '../simulation/types';
import { X, Save, RotateCcw, AlertTriangle, Landmark, Calendar, Shield, BarChart3, Settings } from 'lucide-react';

import { Award, ShieldAlert } from 'lucide-react';

interface PanelControllerProps {
  state: CityState;
  onSetTaxRate: (rate: number) => void;
  onSave: () => void;
  onLoad: () => void;
  onReset: () => void;
  onClose: () => void;
  onTriggerDisaster?: (type: 'tornado' | 'fire_outbreak') => void;
  onTriggerSocialEvent?: (type: 'festival' | 'block_party') => void;
}

type TabType = 'finances' | 'history' | 'services' | 'stats' | 'options';

export const PanelController: React.FC<PanelControllerProps> = ({
  state,
  onSetTaxRate,
  onSave,
  onLoad,
  onReset,
  onClose,
  onTriggerDisaster,
  onTriggerSocialEvent
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('finances');

  // Aggregate statistics
  const roadsCount = Object.keys(state.roads).length;
  const buildingsList = Object.values(state.buildings);
  const buildingsCount = buildingsList.length;

  const resCount = buildingsList.filter(b => b.zone === 'residential').length;
  const comCount = buildingsList.filter(b => b.zone === 'commercial').length;
  const indCount = buildingsList.filter(b => b.zone === 'industrial').length;
  const civicCount = buildingsList.filter(b => b.zone === 'civic' || b.zone === 'park').length;

  const employmentRate = state.population > 0 
    ? Math.round((state.jobs > state.population ? state.population : state.jobs) / state.population * 100)
    : 100;

  // Budget calculations
  const dailyRoadMaintenance = roadsCount * 1;
  const dailyCivicMaintenance = civicCount * 10;
  const totalMaintenanceCost = dailyRoadMaintenance + dailyCivicMaintenance;

  const employed = Math.min(state.population, state.jobs);
  const residentTax = employed * 50 * state.taxRate;
  const commercialTax = employed * 1.5 * 50 * (state.taxRate * 1.2);
  const industrialTax = employed * 2.0 * 50 * (state.taxRate * 1.5);
  const estimatedTaxRevenue = Math.floor(residentTax + commercialTax + industrialTax);
  const netCashFlow = estimatedTaxRevenue - totalMaintenanceCost;

  return (
    <div className="side-panel glass-card" style={{ width: '350px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* HEADER */}
      <div className="panel-title">
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Landmark size={18} style={{ color: '#26a69a' }} />
          <span>Gestione Città</span>
        </span>
        <button className="panel-close" onClick={onClose} title="Chiudi pannello">
          <X size={20} />
        </button>
      </div>

      {/* TAB TOGGLES BAR */}
      <div style={{ display: 'flex', borderBottom: '1px solid #cfd8dc', paddingBottom: '4px', gap: '4px', overflowX: 'auto', pointerEvents: 'auto' }}>
        <button 
          onClick={() => setActiveTab('finances')}
          style={{ padding: '6px 8px', border: 'none', background: activeTab === 'finances' ? '#26a69a' : 'transparent', color: activeTab === 'finances' ? 'white' : '#546e7a', borderRadius: '4px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Landmark size={12} />
          <span>Bilancio</span>
        </button>
        <button 
          onClick={() => setActiveTab('services')}
          style={{ padding: '6px 8px', border: 'none', background: activeTab === 'services' ? '#26a69a' : 'transparent', color: activeTab === 'services' ? 'white' : '#546e7a', borderRadius: '4px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Shield size={12} />
          <span>Servizi</span>
        </button>
        <button 
          onClick={() => setActiveTab('history')}
          style={{ padding: '6px 8px', border: 'none', background: activeTab === 'history' ? '#26a69a' : 'transparent', color: activeTab === 'history' ? 'white' : '#546e7a', borderRadius: '4px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Calendar size={12} />
          <span>Annali</span>
        </button>
        <button 
          onClick={() => setActiveTab('stats')}
          style={{ padding: '6px 8px', border: 'none', background: activeTab === 'stats' ? '#26a69a' : 'transparent', color: activeTab === 'stats' ? 'white' : '#546e7a', borderRadius: '4px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <BarChart3 size={12} />
          <span>Statistiche</span>
        </button>
        <button 
          onClick={() => setActiveTab('options')}
          style={{ padding: '6px 8px', border: 'none', background: activeTab === 'options' ? '#26a69a' : 'transparent', color: activeTab === 'options' ? 'white' : '#546e7a', borderRadius: '4px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Settings size={12} />
          <span>Opzioni</span>
        </button>
      </div>

      {/* ACTIVE CONTENT */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, overflowY: 'auto' }}>
        
        {/* TAB 1: FINANCES */}
        {activeTab === 'finances' && (
          <>
            <div className="slider-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Aliquota Tasse</label>
                <span style={{ fontSize: '14px', fontWeight: 800, color: '#26a69a' }}>
                  {Math.round(state.taxRate * 100)}%
                </span>
              </div>
              <input 
                type="range" 
                min="0.01" 
                max="0.30" 
                step="0.01"
                value={state.taxRate}
                onChange={(e) => onSetTaxRate(parseFloat(e.target.value))}
                className="slider-control"
              />
              <p style={{ fontSize: '11px', color: '#78909c', fontStyle: 'italic' }}>
                *Tasse elevate (&gt;15%) deprimono la felicità ed allontanano i cittadini.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
              <h4 style={{ fontSize: '12px', fontWeight: 800, color: '#37474f', borderBottom: '1px solid #cfd8dc', paddingBottom: '4px' }}>
                Riepilogo Finanziario
              </h4>
              <div className="stat-row">
                <span>Entrate Erariali:</span>
                <span className="stat-val" style={{ color: '#2e7d32' }}>+{estimatedTaxRevenue} §</span>
              </div>
              <div className="stat-row">
                <span>Manutenzione Strade:</span>
                <span className="stat-val" style={{ color: '#c62828' }}>-{dailyRoadMaintenance} §</span>
              </div>
              <div className="stat-row">
                <span>Manutenzione Civica:</span>
                <span className="stat-val" style={{ color: '#c62828' }}>-{dailyCivicMaintenance} §</span>
              </div>
              <div className="stat-row" style={{ borderTop: '2px solid #cfd8dc', paddingTop: '6px', marginTop: '4px' }}>
                <span>Flusso di Cassa:</span>
                <span className="stat-val" style={{ color: netCashFlow >= 0 ? '#2e7d32' : '#c62828', fontSize: '15px' }}>
                  {netCashFlow >= 0 ? '+' : ''}{netCashFlow} §/g
                </span>
              </div>
            </div>
          </>
        )}

        {/* TAB 2: SERVICES */}
        {activeTab === 'services' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Education */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 800, color: '#546e7a' }}>
                <span>Istruzione (Scuole):</span>
                <span>{state.educationCoverage}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', background: '#eceff1', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${state.educationCoverage}%`, height: '100%', background: '#4db6ac' }}></div>
              </div>
            </div>

            {/* Health */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 800, color: '#546e7a' }}>
                <span>Sanità (Ospedali):</span>
                <span>{state.healthCoverage}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', background: '#eceff1', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${state.healthCoverage}%`, height: '100%', background: '#ff7043' }}></div>
              </div>
            </div>

            {/* Safety */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 800, color: '#546e7a' }}>
                <span>Sicurezza (Polizia/Vigili):</span>
                <span>{state.safetyCoverage}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', background: '#eceff1', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${state.safetyCoverage}%`, height: '100%', background: '#5c6bc0' }}></div>
              </div>
            </div>

            <div style={{ background: '#e0f2f1', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#00695c', marginTop: '4px', fontStyle: 'italic', lineHeight: '15px' }}>
              *I servizi municipali attirano investitori ad alto budget, sbloccando enormi grattacieli commerciali ed appartamenti signorili!
            </div>
          </div>
        )}

        {/* TAB 3: TIMELINE */}
        {activeTab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
            {state.history.length === 0 ? (
              <p style={{ fontSize: '12px', color: '#90a4ae', textAlign: 'center' }}>Nessun annale storico registrato.</p>
            ) : (
              state.history.map((h, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', borderLeft: '3px solid #26a69a', paddingLeft: '8px', gap: '2px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 800, color: '#78909c' }}>{h.date}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#37474f' }}>{h.description}</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* TAB 4: STATS */}
        {activeTab === 'stats' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="stat-row">
              <span>Status Era:</span>
              <span className="stat-val" style={{ textTransform: 'uppercase', color: '#00897b' }}>{state.progressionTier}</span>
            </div>
            <div className="stat-row">
              <span>Tasso Occupazione:</span>
              <span className="stat-val">{employmentRate}%</span>
            </div>
            <div className="stat-row">
              <span>Strade ed Incroci:</span>
              <span className="stat-val">{roadsCount}</span>
            </div>
            <div className="stat-row" style={{ borderBottom: '1px solid #cfd8dc', paddingBottom: '4px' }}>
              <span>Edifici Totali:</span>
              <span className="stat-val">{buildingsCount}</span>
            </div>
            <div style={{ paddingLeft: '12px', fontSize: '12px', color: '#78909c', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className="stat-row">
                <span>• Residenziale:</span>
                <span>{resCount}</span>
              </div>
              <div className="stat-row">
                <span>• Commerciale:</span>
                <span>{comCount}</span>
              </div>
              <div className="stat-row">
                <span>• Industriale:</span>
                <span>{indCount}</span>
              </div>
              <div className="stat-row">
                <span>• Servizi & Parchi:</span>
                <span>{civicCount}</span>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: OPTIONS */}
        {activeTab === 'options' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'auto' }}>
            
            {/* Core Save/Load options */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-action-primary" onClick={onSave} style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center', width: '50%', cursor: 'pointer' }}>
                <Save size={14} />
                <span>Salva</span>
              </button>
              <button className="btn-action" onClick={onLoad} style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center', backgroundColor: '#546e7a', width: '50%', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                <RotateCcw size={14} />
                <span>Carica</span>
              </button>
            </div>

            {/* MAYOR SOCIAL EVENTS (Phase 9) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: 'rgba(255,255,255,0.04)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)', marginTop: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#e0f2f1', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Award size={13} style={{ color: '#ffeb3b' }} />
                <span>EVENTI DEL SINDACO</span>
              </span>
              <p style={{ fontSize: '10px', color: '#b0bec5', margin: '2px 0 6px 0' }}>Finanzia manifestazioni per dare un forte impulso a felicità e commercio.</p>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  onClick={() => onTriggerSocialEvent?.('block_party')}
                  style={{ flex: 1, padding: '8px', fontSize: '10px', fontWeight: 800, border: 'none', borderRadius: '5px', background: '#0288d1', color: 'white', cursor: 'pointer' }}
                >
                  Festa Rione (§200)
                </button>
                <button 
                  onClick={() => onTriggerSocialEvent?.('festival')}
                  style={{ flex: 1, padding: '8px', fontSize: '10px', fontWeight: 800, border: 'none', borderRadius: '5px', background: '#3949ab', color: 'white', cursor: 'pointer' }}
                >
                  Festival (§450)
                </button>
              </div>
            </div>

            {/* CRITICAL CRISES / DISASTERS DEMO (Phase 9) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: 'rgba(229, 57, 53, 0.05)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(229, 57, 53, 0.15)' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#ef9a9a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <ShieldAlert size={13} style={{ color: '#ef5350' }} />
                <span>CRISI E DISASTRI NATURALI</span>
              </span>
              <p style={{ fontSize: '10px', color: '#cfd8dc', margin: '2px 0 6px 0' }}>Metti alla prova i tuoi servizi d'emergenza simulando una catastrofe.</p>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  onClick={() => onTriggerDisaster?.('fire_outbreak')}
                  style={{ flex: 1, padding: '8px', fontSize: '10px', fontWeight: 800, border: 'none', borderRadius: '5px', background: '#e53935', color: 'white', cursor: 'pointer' }}
                >
                  Epidemia Roghi
                </button>
                <button 
                  onClick={() => onTriggerDisaster?.('tornado')}
                  style={{ flex: 1, padding: '8px', fontSize: '10px', fontWeight: 800, border: 'none', borderRadius: '5px', background: '#d84315', color: 'white', cursor: 'pointer' }}
                >
                  Scatena Tornado
                </button>
              </div>
            </div>

            <button className="btn-action" onClick={onReset} style={{ backgroundColor: '#c62828', color: 'white', fontWeight: 800, cursor: 'pointer', border: 'none', padding: '10px', borderRadius: '6px', marginTop: '4px' }}>
              Azzera Città
            </button>
            <p style={{ fontSize: '10px', color: '#90a4ae', textAlign: 'center', marginTop: '8px', margin: 0 }}>
              SWISSLAND 3D - Pocket City Overhaul<br />
              Vite, React & Three.js.
            </p>
          </div>
        )}

      </div>

      {state.treasury < 0 && (
        <div style={{ display: 'flex', gap: '8px', padding: '10px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '8px', color: '#c62828', fontSize: '11px', fontWeight: 700, pointerEvents: 'auto' }}>
          <AlertTriangle size={16} />
          <span>BILANCIO IN DEFICIT! Impossibile edificare infrastrutture municipali.</span>
        </div>
      )}
    </div>
  );
};
